import { PrivateRegionSpecInput } from '@/api/type-request'
import {
  ServerPrivateRegionNodeDetails,
  ServerPrivateRegionNodeSpec,
  ServerPrivateRegionVGpuSpec,
} from '@/api/type-server'

import { SpecCategory } from '../constants/specCategory'

// Calculate total resource usage from specs, broken down by spec category
export function calculateResourceUsage(
  specs: ServerPrivateRegionNodeSpec[],
  node: ServerPrivateRegionNodeDetails,
  vgpuSpecsList?: ServerPrivateRegionVGpuSpec[],
) {
  const enabledSpecs = specs.filter((s) => s.enabled)

  // Use node hardware data as total capacity
  const totalCpuCores = node.cpuCores
  const totalRamMB = node.ramMB
  const totalDiskGB = node.diskGB
  const totalGpus = node.gpuNum

  // Separate specs by category
  const vgpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.VGPU)
  const gpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.GPU)
  const cpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.CPU)

  // Calculate GPU usage (Physical GPU)
  const vgpuGpuUsed = vgpuSpecs.reduce((sum, s) => {
    const divisionCount =
      vgpuSpecsList?.find((v) => v.resID === s.vgpuSpecId)?.divisionCount ??
      s.vGpuSpec?.divisionCount ??
      1
    return sum + Math.ceil((s.units * s.gpuNum) / divisionCount)
  }, 0)
  const gpuGpuUsed = gpuSpecs.reduce((sum, s) => sum + s.units * s.gpuNum, 0)

  // Calculate CPU usage by spec category
  const vgpuCpuUsed = vgpuSpecs.reduce((sum, s) => sum + s.units * s.cpuCores, 0)
  const gpuCpuUsed = gpuSpecs.reduce((sum, s) => sum + s.units * s.cpuCores, 0)
  const cpuCpuUsed = cpuSpecs.reduce((sum, s) => sum + s.units * s.cpuCores, 0)

  // Calculate RAM usage by spec category (in MB, will convert to GB)
  const vgpuRamUsed = vgpuSpecs.reduce((sum, s) => sum + s.units * s.ramMB, 0)
  const gpuRamUsed = gpuSpecs.reduce((sum, s) => sum + s.units * s.ramMB, 0)
  const cpuRamUsed = cpuSpecs.reduce((sum, s) => sum + s.units * s.ramMB, 0)

  // Calculate Disk usage by spec category
  const vgpuDiskUsed = vgpuSpecs.reduce((sum, s) => sum + s.units * s.diskGB, 0)
  const gpuDiskUsed = gpuSpecs.reduce((sum, s) => sum + s.units * s.diskGB, 0)
  const cpuDiskUsed = cpuSpecs.reduce((sum, s) => sum + s.units * s.diskGB, 0)

  return {
    cpu: {
      vgpuSpecUsed: vgpuCpuUsed,
      gpuSpecUsed: gpuCpuUsed,
      cpuSpecUsed: cpuCpuUsed,
      total: totalCpuCores,
    },
    ram: {
      vgpuSpecUsed: Math.round(vgpuRamUsed / 1024),
      gpuSpecUsed: Math.round(gpuRamUsed / 1024),
      cpuSpecUsed: Math.round(cpuRamUsed / 1024),
      total: Math.round(totalRamMB / 1024),
    }, // Convert to GB
    disk: {
      vgpuSpecUsed: vgpuDiskUsed,
      gpuSpecUsed: gpuDiskUsed,
      cpuSpecUsed: cpuDiskUsed,
      total: totalDiskGB,
    },
    gpu: { vgpuUsed: vgpuGpuUsed, gpuUsed: gpuGpuUsed, total: totalGpus },
  }
}

// Convert ServerPrivateRegionNodeSpec to API input format
export function specToInput(spec: ServerPrivateRegionNodeSpec): PrivateRegionSpecInput {
  return {
    res_id: spec.resID,
    category: spec.category,
    units: spec.units,
    cpu_cores: spec.cpuCores,
    ram_mb: spec.ramMB,
    disk_gb: spec.diskGB,
    gpu_num: spec.gpuNum,
    vgpu_spec_id: spec.vgpuSpecId || undefined,
    enabled: spec.enabled,
  }
}
